import {
  Card,
  Flex,
  JaneGoldIcon,
  Link,
  PartnerLogo,
  Typography,
} from '@iheartjane/reefer';
import type { TypographyVariant } from '@iheartjane/reefer';
import type { BrandSpecialUserSegments, Breadcrumb } from '@iheartjane/types';
import { useMemo } from 'react';

import { brandSpecialHasSegments } from '../../utils/gold/brandSpecialHasSegments';

import styles from './janeGoldCard.module.css';

export interface JaneGoldCardProps {
  /** Whether the user is authenticated */
  authenticated?: boolean;

  /** Optionally override the background color of the top of the card */
  backgroundColor?: string;

  /** Brand logo URL */
  brandImage?: string | null;

  /** Brand name displayed below the image */
  brandName: string;

  /** Breadcrumb navigation data for the Link component */
  breadcrumbs?: Breadcrumb[];

  /** Href for use outside of react-router */
  href?: string;

  /** Optional image URL for the special. When absent, shows a JaneGoldIcon + tagLabel placeholder */
  imageSrc?: string | null;

  /** Whether this is a bundle special (shows "Bundle" subtitle) */
  isBundle?: boolean;

  /** Click handler (e.g. analytics tracking). Fires on the card element alongside link navigation */
  onClick?: () => void;

  /** User segment targeting for the brand special */
  userSegments?: BrandSpecialUserSegments | null;

  /** Label for the discount/tag shown in the placeholder area (e.g. "20% Off") */
  tagLabel: string;

  /** Typography variant for the tag label */
  tagTypography?: TypographyVariant;

  /** Name/title of the special */
  title: string;

  /** Navigation URL for the card link */
  to?: string;
}

export function JaneGoldCard({
  authenticated,
  backgroundColor,
  brandImage,
  brandName,
  breadcrumbs,
  href,
  imageSrc,
  isBundle = false,
  onClick,
  tagLabel,
  tagTypography = 'h2-semibold',
  title,
  to,
  userSegments,
}: JaneGoldCardProps) {
  const requiresSignIn = useMemo(() => {
    if (authenticated || !userSegments) return false;

    return brandSpecialHasSegments(userSegments);
  }, [authenticated, userSegments]);
  const showSubtitle = requiresSignIn || isBundle;
  const subtitleText = requiresSignIn ? 'Sign in for eligibility' : 'Bundle';

  return (
    <Link
      ariaLabel={`View cash back details for ${title}`}
      {...(breadcrumbs && { state: { breadcrumbs } })}
      className={styles['goldCardLink']}
      data-testid="gold-special-card"
      href={href}
      onClick={onClick}
      to={to}
      variant="minimal"
    >
      <Card
        aria-label={`View cash back details for ${title}`}
        className={styles['goldCard']}
        border="transparent-black-ten"
        flat
        width="100%"
      >
        <div className={styles['imageArea']}>
          {imageSrc ? (
            <Card.Image
              alt={title}
              src={imageSrc}
              sizes="card-lg"
              responsive
              aspectRatio={2 / 1}
            />
          ) : (
            <Flex
              className={styles['placeholderImage']}
              width="100%"
              justifyContent="center"
              alignItems="center"
              style={{
                '--background-color':
                  backgroundColor || 'var(--colors-grays-ultralight)',
              }}
              gap={8}
            >
              <JaneGoldIcon size="lg" />
              <Typography
                branded
                color="inherit"
                variant={tagTypography}
                as="p"
                maxLines={2}
              >
                {tagLabel}
              </Typography>
            </Flex>
          )}
        </div>

        <Card.Content p={16}>
          <Flex gap={16} width="100%">
            <Flex alignSelf="center">
              <PartnerLogo
                size="xs"
                name={brandName || 'brand'}
                image={brandImage}
                variant="brand"
              />
            </Flex>
            <Flex flexDirection="column" width="100%" overflow="hidden">
              <Typography color="grays-mid" truncateAt="100%">
                {brandName}
                {showSubtitle && (
                  <>
                    <Typography mx={4} as="span" color="grays-mid">
                      &#8226;
                    </Typography>
                    <Typography color="grays-mid" as="span" truncateAt="100%">
                      {subtitleText}
                    </Typography>
                  </>
                )}
              </Typography>
              <Typography variant="body-semibold" truncateAt="100%">
                {title}
              </Typography>
            </Flex>
          </Flex>
        </Card.Content>
      </Card>
    </Link>
  );
}
